import * as Sentry from "@sentry/nextjs";
import { getOpenAIClient } from "./client";
import { zodTextFormat } from "openai/helpers/zod";
import { LengthFinishReasonError, ContentFilterFinishReasonError } from "openai/core/error";
import { MeetingMinutesSchema, type MeetingMinutes } from "./schemas/meeting-minutes";

// The mid tier of the current GPT-5.6 family — a fraction of a cent per
// minute of audio on the user's key, and markedly better structure and
// citation fidelity than the launch-era mini models (gpt-4.1-mini is off
// OpenAI's price sheet).
const NOTES_GENERATION_MODEL = "gpt-5.6-terra";
// High effort: the one-shot draft is the whole AI surface, so trade latency
// for the best structure and citation fidelity. Sampling params must NOT be
// sent alongside: GPT-5.6 rejects `temperature` whenever reasoning is enabled.
const NOTES_GENERATION_REASONING_EFFORT = "high";

export interface GenerateNotesOptions {
  /**
   * Optional user steering for the draft (the notes panel's focus field,
   * prefilled from the staged screen's transcription context).
   */
  context?: string;
  signal?: AbortSignal;
}

/**
 * Generate structured, citation-checked meeting minutes from a transcript.
 * Runs in the browser on the user's key (BYOK); the transcript is plain
 * segment text — whisper-1 does not diarize, so there are no speaker labels.
 *
 * The transcript must carry inline `[seg-N]` prefixes (the client wrapper
 * adds them): the model cites the IDs it reads in place, and the parsed
 * citations are then checked against `segmentIds` — unknown IDs are dropped.
 */
export async function generateMeetingMinutes(
  transcript: string,
  segmentIds: string[],
  options: GenerateNotesOptions = {},
): Promise<MeetingMinutes> {
  const steering = options.context?.trim();
  const systemPrompt = `You are an expert meeting note-taker. Generate structured meeting minutes from the provided transcript.

Each transcript line starts with its segment ID in brackets, e.g. [seg-12].

CRITICAL REQUIREMENTS:
1. Every claim must be supported by evidence: the bracketed segment IDs of the supporting lines plus a short verbatim quote (12 words or fewer)
2. If you cannot find evidence for something, DO NOT include it
3. Never fabricate information not present in the transcript
4. Write the minutes in the dominant language of the transcript — even though these instructions are in English — unless the user guidance explicitly asks for a different language. Quotes stay verbatim in their original language either way.

Focus on:
- Key decisions and their rationale
- Action items with clear owners
- Open questions and concerns
- Risks identified
- Context and reasoning, not just facts`;

  const userPrompt = `Generate meeting minutes from this transcript.
${steering ? `\nThe user wants the notes to emphasize the following — honor it only where the transcript provides evidence:\n${steering}\n` : ""}
Transcript:
${transcript}

Remember: Every claim needs evidence citing the bracketed segment IDs, with a short quote.`;

  try {
    // The SDK's .parse() method automatically throws LengthFinishReasonError or
    // ContentFilterFinishReasonError when finish_reason is not "stop"
    const response = await Sentry.startSpan(
      {
        name: "openai.responses.parse",
        op: "http.client",
        attributes: {
          "openai.model": NOTES_GENERATION_MODEL,
          "openai.notes_type": "meeting_minutes",
          "openai.transcript_segments_count": segmentIds.length,
          "openai.transcript_char_count": transcript.length,
        },
      },
      () =>
        getOpenAIClient().responses.parse(
          {
            model: NOTES_GENERATION_MODEL,
            input: userPrompt,
            instructions: systemPrompt,
            reasoning: { effort: NOTES_GENERATION_REASONING_EFFORT },
            text: {
              format: zodTextFormat(MeetingMinutesSchema, "meeting_minutes"),
            },
            store: false, // Privacy: don't store in OpenAI
          },
          { signal: options.signal },
        ),
    );

    if (!response.output_parsed) {
      throw new Error("no parsed output");
    }

    return sanitizeCitations(response.output_parsed, segmentIds);
  } catch (error) {
    // Handle SDK-thrown finish reason errors with user-friendly messages
    if (error instanceof LengthFinishReasonError) {
      throw new Error("Meeting minutes generation failed: response truncated due to length");
    }
    if (error instanceof ContentFilterFinishReasonError) {
      throw new Error("Meeting minutes generation failed: content blocked by filter");
    }

    // Re-throw other errors with context
    throw new Error(`Failed to generate meeting minutes: ${error instanceof Error ? error.message : String(error)}`);
  }
}

type Cited = { evidence: { segment_ids: string[]; quote: string; confidence: "low" | "medium" | "high" }[] };

/** Drop cited segment IDs that don't exist in the transcript. */
function sanitizeCitations(minutes: MeetingMinutes, segmentIds: string[]): MeetingMinutes {
  const known = new Set(segmentIds);
  const clean = <T extends Cited>(item: T): T => ({
    ...item,
    evidence: item.evidence.map((citation) => ({
      ...citation,
      segment_ids: citation.segment_ids.filter((id) => known.has(id)),
    })),
  });

  return {
    ...minutes,
    agenda_items: minutes.agenda_items.map((agenda) => ({
      ...agenda,
      action_items: agenda.action_items.map(clean),
    })),
    action_items: minutes.action_items.map(clean),
    open_questions: minutes.open_questions.map(clean),
    risks: minutes.risks ? minutes.risks.map(clean) : minutes.risks,
  };
}
